import * as React from "react";

import { TopNav } from "./TopNav";
import { PageToolbar } from "./PageToolbar";
import { CommandPalette } from "./CommandPalette";
import { AddHoldingDialog } from "../AddHoldingDialog";
import { useV2Actions } from "../../../context/V2ActionsContext";
import { useAuth } from "../../../AuthContext";
import { usePortfolio } from "../../../hooks/usePortfolio";
import { useUnreadCount } from "../../../hooks/useUnreadCount";
import { useInsightsUnreadCount } from "../../../hooks/useInsightsUnreadCount";

/**
 * AppLayout - Main layout wrapper for V2 UI
 *
 * Institutional Console shell (DESIGN-SPEC.md §3, issue #232): composes a
 * 52px TopNav + 46px PageToolbar + ⌘K CommandPalette, replacing the 256px
 * AppSidebar. Functionality carried over 1:1 from the old sidebar shell:
 *
 * - V2ActionsProvider wiring (unchanged — provided by each route in App.js;
 *   this component only *consumes* useV2Actions()).
 * - GlobalDialogs (AddHoldingDialog), triggered by Quick Actions in the
 *   command palette exactly as it was by the sidebar's Quick Actions group.
 * - handleAction dispatch (add-holding, take-snapshot) — now wired through
 *   CommandPalette instead of AppSidebar's QuickActionsSection.
 * - Settings access: rehomed to the TopNav gear icon (routes to /settings).
 * - Theme switching: still lives on the Settings > Appearance theme cards
 *   (unchanged by this issue — TopNav does not duplicate theme controls).
 * - User identity + Logout: rehomed to a small account menu opened by the
 *   TopNav avatar button (the old AppSidebar footer's UserSection had no
 *   equivalent elsewhere once the sidebar was removed — see TopNav.jsx).
 * - Unread badges: Insights (6px accent dot on the Insights nav item) and
 *   Events (7px red dot on the bell) replace the sidebar's CountBadge pills.
 *
 * Sub-tab routing is derived from the existing flat/param route table
 * (unchanged, per CC4) via PageToolbar — no new routes are introduced here.
 */

function AppMain({ children, className = "" }) {
  return (
    <main className={`flex-1 overflow-auto p-4 md:p-6 ${className}`.trim()}>
      {children}
    </main>
  );
}

function GlobalDialogs() {
  const { isAddHoldingOpen, setIsAddHoldingOpen } = useV2Actions();
  const { refresh } = usePortfolio();

  const handleAddHoldingSuccess = React.useCallback(async () => {
    // Refresh portfolio data after adding a new holding
    await refresh();
  }, [refresh]);

  return (
    <AddHoldingDialog
      open={isAddHoldingOpen}
      onOpenChange={setIsAddHoldingOpen}
      onSuccess={handleAddHoldingSuccess}
    />
  );
}

export function AppLayout({
  children,
  title,
  headerContent,
  primaryAction,
  secondaryActions,
}) {
  const { handleAction } = useV2Actions();
  const { user } = useAuth();
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const { count: eventsUnreadCount, refresh: refreshEventsUnread } = useUnreadCount();
  const { count: insightsUnreadCount, refresh: refreshInsightsUnread } = useInsightsUnreadCount();

  // Global ⌘K / Ctrl+K listener (DESIGN-SPEC.md §6) — opens the command
  // palette from anywhere in the V2 shell. Esc-to-close is handled natively
  // by cmdk's underlying Radix Dialog.
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      const isCombo = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
      if (isCombo) {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Refresh unread counts when navigating to /events or /insights (mirrors
  // the previous AppSidebar behavior — user may have just marked items read).
  React.useEffect(() => {
    if (window.location.pathname === "/events") {
      const timer = setTimeout(() => refreshEventsUnread(), 2000);
      return () => clearTimeout(timer);
    }
    if (window.location.pathname === "/insights") {
      const timer = setTimeout(() => refreshInsightsUnread(), 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const handleUnreadCountChanged = () => refreshEventsUnread();
    const handleInsightsUnreadCountChanged = () => refreshInsightsUnread();

    window.addEventListener("unread-count-changed", handleUnreadCountChanged);
    window.addEventListener("insights-unread-count-changed", handleInsightsUnreadCountChanged);
    return () => {
      window.removeEventListener("unread-count-changed", handleUnreadCountChanged);
      window.removeEventListener("insights-unread-count-changed", handleInsightsUnreadCountChanged);
    };
  }, [refreshEventsUnread, refreshInsightsUnread]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <TopNav
        insightsUnreadCount={insightsUnreadCount}
        eventsUnreadCount={eventsUnreadCount}
        onOpenPalette={() => setPaletteOpen(true)}
        mobileMenuOpen={mobileMenuOpen}
        onMobileMenuOpenChange={setMobileMenuOpen}
      />
      <PageToolbar
        title={title}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions || headerContent}
        eventsUnreadCount={eventsUnreadCount}
      />
      <AppMain>{children}</AppMain>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onAction={handleAction}
        isAdmin={!!user?.isAdmin}
        insightsUnreadCount={insightsUnreadCount}
        eventsUnreadCount={eventsUnreadCount}
      />

      <GlobalDialogs />
    </div>
  );
}

// Export sub-components for flexible usage
export { AppMain };
